import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Package, 
  Percent, 
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Check,
  Upload,
  Image
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import ImageUpload from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [packageSettings, setPackageSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      // Get full package list
      const [listRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/packages/list`),
        axios.get(`${API}/admin/packages`)
      ]);
      setPackages(listRes.data);
      
      // Map settings by ID
      const settingsMap = {};
      settingsRes.data.forEach(pkg => {
        settingsMap[pkg.id] = {
          deposit_percentage: pkg.deposit_percentage,
          is_deposit_only: pkg.is_deposit_only
        };
      });
      setPackageSettings(settingsMap);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/admin/packages/${packageId}`);
      toast.success("Package deleted");
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete package");
    }
  };

  const handleToggleDeposit = async (packageId, isDeposit) => {
    try {
      await axios.put(`${API}/admin/packages/${packageId}`, {
        is_deposit_only: isDeposit,
        deposit_percentage: packageSettings[packageId]?.deposit_percentage || 100
      });
      setPackageSettings({
        ...packageSettings,
        [packageId]: {
          ...packageSettings[packageId],
          is_deposit_only: isDeposit
        }
      });
      toast.success("Deposit setting updated");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleSaveDeposit = async (packageId, percentage) => {
    try {
      await axios.put(`${API}/admin/packages/${packageId}`, {
        is_deposit_only: packageSettings[packageId]?.is_deposit_only || false,
        deposit_percentage: percentage
      });
      toast.success("Deposit % saved");
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-packages">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Experience Packages
            </h1>
            <p className="text-[#A1A1AA]">Create, edit, and manage your service packages</p>
          </div>
          <button
            onClick={() => {
              setEditingPackage(null);
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-package-btn"
          >
            <Plus className="w-5 h-5" />
            Add Package
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.3)] p-6">
          <h3 className="text-[#D4AF37] font-semibold mb-2">Deposit Settings</h3>
          <ul className="text-[#A1A1AA] text-sm space-y-1">
            <li>• <strong className="text-green-500">Deposit ON</strong>: Customers pay the deposit %, balance collected later</li>
            <li>• <strong className="text-red-500">Deposit OFF</strong>: Customers pay full package price</li>
          </ul>
        </div>

        {/* Packages List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
            <Package className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Packages Yet</h3>
            <p className="text-[#A1A1AA] mb-4">Create your first experience package</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary"
            >
              Add Package
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {packages.map((pkg) => {
              const settings = packageSettings[pkg.id] || { deposit_percentage: 100, is_deposit_only: false };
              
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    {/* Package Image Preview */}
                    {pkg.image && (
                      <div className="flex-shrink-0 w-24 h-24 overflow-hidden border border-[rgba(212,175,55,0.2)]">
                        <img 
                          src={pkg.image} 
                          alt={pkg.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Package Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Package className="w-5 h-5 text-[#D4AF37]" />
                        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                          {pkg.name}
                        </h3>
                        {pkg.is_active === false && (
                          <span className="px-2 py-0.5 bg-red-600/20 text-red-500 text-xs">Inactive</span>
                        )}
                      </div>
                      <p className="text-[#A1A1AA] text-sm mb-2">{pkg.tagline || pkg.description?.substring(0, 100)}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-[#a1a1aa]">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {pkg.price_display || `C$${pkg.price}`}
                        </span>
                        <span>{pkg.duration}</span>
                        <span>{pkg.guests}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      {/* Deposit Toggle */}
                      <div className="flex items-center gap-3">
                        <span className="text-[#A1A1AA] text-sm">Deposit:</span>
                        <button
                          onClick={() => handleToggleDeposit(pkg.id, !settings.is_deposit_only)}
                          className={`relative w-14 h-7 rounded-full transition-colors ${
                            settings.is_deposit_only ? 'bg-green-600' : 'bg-[#52525B]'
                          }`}
                        >
                          <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                            settings.is_deposit_only ? 'left-8' : 'left-1'
                          }`} />
                        </button>
                      </div>

                      {/* Deposit Percentage */}
                      {settings.is_deposit_only && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={settings.deposit_percentage}
                            onChange={(e) => {
                              setPackageSettings({
                                ...packageSettings,
                                [pkg.id]: {
                                  ...settings,
                                  deposit_percentage: Number(e.target.value)
                                }
                              });
                            }}
                            onBlur={(e) => handleSaveDeposit(pkg.id, Number(e.target.value))}
                            className="w-20 bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 text-center focus:border-[#D4AF37] focus:outline-none"
                            min="1"
                            max="100"
                          />
                          <Percent className="w-4 h-4 text-[#a1a1aa]" />
                        </div>
                      )}

                      {/* Edit/Delete */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingPackage(pkg);
                            setShowModal(true);
                          }}
                          className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                          title="Edit Package"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePackage(pkg.id)}
                          className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-red-500 hover:border-red-500 transition-colors"
                          title="Delete Package"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Deposit Preview */}
                  {settings.is_deposit_only && pkg.price > 0 && (
                    <div className="mt-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                      <p className="text-sm text-[#A1A1AA]">
                        Customer pays: <strong className="text-[#D4AF37]">C${(pkg.price * settings.deposit_percentage / 100).toFixed(2)}</strong> deposit
                        <span className="text-[#a1a1aa]"> (Balance: C${(pkg.price - pkg.price * settings.deposit_percentage / 100).toFixed(2)} due later)</span>
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Package Modal */}
        {showModal && (
          <PackageModal
            package={editingPackage}
            onClose={() => {
              setShowModal(false);
              setEditingPackage(null);
            }}
            onSave={() => {
              setShowModal(false);
              setEditingPackage(null);
              fetchPackages();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Package Modal Component
const PackageModal = ({ package: pkg, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: pkg?.name || "",
    tagline: pkg?.tagline || "",
    description: pkg?.description || "",
    price: pkg?.price || 0,
    price_display: pkg?.price_display || "",
    duration: pkg?.duration || "",
    guests: pkg?.guests || "",
    features: pkg?.features || pkg?.includes || [],
    image: pkg?.image || "",
    is_active: pkg?.is_active !== false
  });
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image: response.data.url });
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error("Name and price are required");
      return;
    }

    setSaving(true);

    try {
      if (pkg) {
        // Update existing
        await axios.put(`${API}/admin/packages/${pkg.id}/details`, formData);
        toast.success("Package updated");
      } else {
        // Create new
        await axios.post(`${API}/admin/packages/create`, {
          ...formData,
          price_display: formData.price_display || `from C$${formData.price}`
        });
        toast.success("Package created");
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-2xl my-8"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)] flex items-center justify-between">
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            {pkg ? "Edit Package" : "Create Package"}
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="form-label">Package Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="e.g., Signature Experience"
              required
            />
          </div>

          <div>
            <label className="form-label">Tagline</label>
            <input
              type="text"
              value={formData.tagline}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="A short catchy description"
            />
          </div>

          {/* Package Image */}
          <div>
            <ImageUpload
              label="Package Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              folder="packages"
              placeholder="Upload image or enter URL"
              previewHeight="h-40"
            />
            <p className="text-xs text-[#a1a1aa] mt-1">Recommended: 800x600px, max 5MB. JPG, PNG, or WebP.</p>
          </div>

          <div>
            <label className="form-label">Full Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">Price (C$) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="form-label">Price Display</label>
              <input
                type="text"
                value={formData.price_display}
                onChange={(e) => setFormData({ ...formData, price_display: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                placeholder="from C$499"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">Duration</label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                placeholder="2-3 hours"
              />
            </div>
            <div>
              <label className="form-label">Guests</label>
              <input
                type="text"
                value={formData.guests}
                onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                placeholder="Up to 100 guests"
              />
            </div>
          </div>

          {/* Features */}
          <div>
            <label className="form-label">Features / What's Included</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                placeholder="Add a feature..."
              />
              <button
                type="button"
                onClick={addFeature}
                className="btn-secondary px-4"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                  <span className="flex-1 text-[#F5F5F0] text-sm">{feature}</span>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-[#a1a1aa] hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-4">
            <label className="text-[#A1A1AA]">Active:</label>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                formData.is_active ? 'bg-green-600' : 'bg-[#52525B]'
              }`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                formData.is_active ? 'left-8' : 'left-1'
              }`} />
            </button>
            <span className="text-[#a1a1aa] text-sm">
              {formData.is_active ? "Visible on website" : "Hidden from website"}
            </span>
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {pkg ? "Update Package" : "Create Package"}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminPackagesPage;
